import type { StationBrand } from '@centric-os/types';
import { DateTime } from 'luxon';
import { cloneDeep } from 'lodash-es';

export default () => {
  /**
   * Converts a time string into minutes.
   * @param timeString - The time string in "hh:mm:ss" format.
   * @returns The total minutes.
   */
  const parseTimeStringIntoMinutes = (timeString: string | undefined): number => {
    if (!timeString) return 0;
    const [hours, mins] = timeString.split(':').map(Number);
    return hours * 60 + mins;
  };

  /**
   * Converts a user-defined delivery timeslot into a readable string.
   * @param userDefinedDeliveryTimeslot - The user-defined delivery timeslot object.
   * @returns A string representing the delivery timeslot in "hh:mma - hh:mma" format.
   */
  const parseUserDefinedDeliveryTimeslotToString = (userDefinedDeliveryTimeslot?: {
    start_time: string;
    end_time: string;
  }): string => {
    if (!userDefinedDeliveryTimeslot) return '';
    const { start_time, end_time } = userDefinedDeliveryTimeslot;
    const startString = DateTime.fromFormat(start_time, 'HH:mm').toFormat('hh:mma').toLowerCase();
    const endString = DateTime.fromFormat(end_time, 'HH:mm').toFormat('hh:mma').toLowerCase();
    return `${startString} - ${endString}`;
  };

  /**
   * Converts minutes into "HH:MM:SS" format.
   * @param minutes - The total minutes.
   * @returns A string representing the time in "HH:MM:SS" format.
   */
  const minutesToHHMMSS = (minutes: number): string => {
    if (!minutes) return '00:00:00';
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${String(hours).padStart(2, '0')}:${String(remainingMinutes).padStart(2, '0')}:00`;
  };

  /**
   * Prepares delivery destinations for saving.
   * @param formValues - The form values containing delivery destinations.
   * @returns An object with `delivery_destinations` and `delivery_destinations_automatic_timeslot` arrays.
   */
  const prepDeliveryDestinationsForSave = (formValues: StationBrand): Record<string, string[]> => {
    const customDeliveryDestinations: string[] = [];
    const automaticDestinations: string[] = [];

    if (formValues.timeslots?.delivery_is_user_defined) {
      const mappedCustomDeliveryDestinations =
        formValues?.timeslots?.delivery_user_defined.reduce<string[]>(
          (acc, userDefinedTimeslot) => {
            userDefinedTimeslot?.delivery_destinations.forEach((destination) => {
              if (!acc.includes(destination)) acc.push(destination);
            });
            return acc;
          },
          [],
        ) || [];

      customDeliveryDestinations.push(...mappedCustomDeliveryDestinations);
    } else {
      const mappedAutomaticDestinations = cloneDeep(formValues.timeslots?.delivery_destinations);
      automaticDestinations.push(...mappedAutomaticDestinations);
    }

    return {
      delivery_destinations: customDeliveryDestinations,
      delivery_destinations_automatic_timeslot: automaticDestinations,
    };
  };

  return {
    parseTimeStringIntoMinutes,
    parseUserDefinedDeliveryTimeslotToString,
    minutesToHHMMSS,
    prepDeliveryDestinationsForSave,
  };
};
